import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getPublishedPosts } from "../../utils/posts";

/* ── Tilt Card ── */
function TiltCard({ children, style }) {
  const ref = useRef(null);
  const handleMove = (e) => {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
    const dy = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);
    el.style.transform = `perspective(800px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg) translateZ(6px)`;
  };
  const handleLeave = () => {
    const el = ref.current; if (!el) return;
    el.style.transform = "perspective(800px) rotateY(0deg) rotateX(0deg) translateZ(0)";
  };
  return (
    <div ref={ref} onMouseMove={handleMove} onMouseLeave={handleLeave}
      style={{ transition: "transform 0.15s ease", transformStyle: "preserve-3d", willChange: "transform", ...style }}>
      {children}
    </div>
  );
}

/* ── Particle Canvas ── */
function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, w, h;
    const resize = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * 1400, y: Math.random() * 800,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.5 + 0.5,
      color: Math.random() > 0.6 ? "#f5c518" : Math.random() > 0.5 ? "#c9a84c" : "#ffffff",
      alpha: Math.random() * 0.5 + 0.1,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / 100) * 0.08})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        const col = p.color === "#f5c518" ? `245,197,24` : p.color === "#c9a84c" ? `201,168,76` : `255,255,255`;
        ctx.fillStyle = `rgba(${col},${p.alpha})`;
        ctx.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity: 0.6, zIndex: 0 }} />;
}

export default function MemberDashboard() {
  const { user, userProfile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState({ weekly: null, insights: null, trade: null, education: null });
  const [loading, setLoading] = useState(true);
  const [vis, setVis] = useState(false);

  useEffect(() => { setTimeout(() => setVis(true), 80); }, []);

  useEffect(() => {
    if (!userProfile || userProfile.status === "pending") return;
    (async () => {
      try {
        const all = await getPublishedPosts({ limit: 50 });
        const find = (cat) => all.find(p => p.category === cat) || null;
        setPosts({
          weekly: find("weekly-outlook"),
          insights: find("macro-notes") || find("insights"),
          trade: find("trade-ideas"),
          education: find("education"),
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [userProfile]);

  if (!user) { navigate("/login"); return null; }
  if (isAdmin) { navigate("/admin"); return null; }

  const handleLogout = async () => { await logout(); navigate("/"); };

  // ── Pending ──
  if (!userProfile || userProfile.status === "pending") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#050505", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "20%", left: "20%", width: 600, height: 400, borderRadius: "50%", background: "radial-gradient(ellipse,#f5c51806 0%,transparent 70%)", animation: "glowOrb 8s ease-in-out infinite", pointerEvents: "none" }} />
        <style>{`@keyframes glowOrb{0%,100%{opacity:.35}50%{opacity:.8}} @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 500, padding: "0 24px" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f5c51812", border: "1px solid #f5c51835", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", boxShadow: "0 0 32px #f5c51820" }}>
            <span style={{ fontSize: "1.6rem" }}>⏳</span>
          </div>
          <h2 style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1.5rem", textTransform: "uppercase", color: "#f0f0f0", marginBottom: 16 }}>Awaiting Approval</h2>
          <p style={{ fontSize: "15px", color: "#555", lineHeight: 1.85, marginBottom: 32 }}>Your account is pending admin approval. You will be notified once access is granted.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <Link to="/" style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#c9a84c", textDecoration: "none", padding: "10px 20px", border: "1px solid #c9a84c30", borderRadius: "6px" }}>← Home</Link>
            <button onClick={handleLogout} style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#444", background: "transparent", border: "1px solid #1e1e1e", borderRadius: "6px", padding: "10px 20px", cursor: "pointer" }}>Logout</button>
          </div>
        </div>
      </div>
    );
  }

  // basePath must match exactly the routes in App.js
  const sections = [
    { key: "weekly",    to: "/weekly-outlook", basePath: "/weekly-outlook", label: "Weekly Outlook", icon: "◎", color: "#f5c518", desc: "Latest weekly market analysis",  tag: "Every Monday" },
    { key: "insights",  to: "/insights",        basePath: "/insights",       label: "Macro Notes",   icon: "◈", color: "#818cf8", desc: "In-depth macro research",         tag: "Research"     },
    { key: "trade",     to: "/trade-ideas",     basePath: "/trade-ideas",    label: "Trade Ideas",   icon: "⚡", color: "#4ade80", desc: "High-probability setups",        tag: "Setups"       },
    { key: "education", to: "/education",       basePath: "/education",      label: "Education",     icon: "⊕", color: "#f87171", desc: "Frameworks and concepts",        tag: "Learn"        },
  ];

  const formatDate = (ts) => {
    if (!ts?.seconds) return null;
    return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#050505", paddingTop: 80, position: "relative", overflowX: "hidden" }}>
      <style>{`
        @keyframes glowOrb   { 0%,100%{opacity:.35} 50%{opacity:.8} }
        @keyframes fadeUp    { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lineIn    { from{transform:scaleX(0)} to{transform:scaleX(1)} }
        @keyframes pulse     { 0%,100%{box-shadow:0 0 0 0 #4ade8055} 60%{box-shadow:0 0 0 8px #4ade8000} }
        @keyframes floatY    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes shimmer   { 0%,100%{opacity:.3} 50%{opacity:.6} }
        @keyframes ringPulse { 0%,100%{transform:scale(1);opacity:.15} 50%{transform:scale(1.08);opacity:.3} }
        @media(max-width:700px){ .dash-grid{grid-template-columns:1fr!important} }
      `}</style>

      <ParticleCanvas />
      <div style={{ position: "fixed", top: "10%", right: "5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(ellipse,#c9a84c08 0%,transparent 70%)", animation: "glowOrb 8s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", bottom: "10%", left: "0%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(ellipse,#818cf806 0%,transparent 70%)", animation: "glowOrb 11s ease-in-out infinite reverse", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 700, borderRadius: "50%", border: "1px solid #f5c51808", animation: "ringPulse 6s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />

      <div className="container" style={{ paddingBottom: 100, position: "relative", zIndex: 1 }}>

        {/* ── HEADER ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 48, flexWrap: "wrap", gap: 16, opacity: vis ? 1 : 0, animation: vis ? "fadeUp 0.6s 0.05s ease both" : "none" }}>
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.26em", textTransform: "uppercase", color: "#c9a84c", marginBottom: 12 }}>Members Area</div>
            <h1 style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "clamp(2rem,5vw,3.2rem)", textTransform: "uppercase", color: "#f0f0f0", lineHeight: 1, margin: 0 }}>
              Welcome, <span style={{ color: "#f5c518", textShadow: "0 0 60px #f5c51840" }}>{userProfile.name || "Trader"}</span>
            </h1>
            <div style={{ height: 2, width: 60, background: "linear-gradient(90deg,#f5c518,transparent)", marginTop: 16, transformOrigin: "left", animation: "lineIn 0.8s 0.3s ease both" }} />
          </div>
          <button onClick={handleLogout}
            style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#444", background: "transparent", border: "1px solid #1e1e1e", borderRadius: "6px", padding: "10px 20px", cursor: "pointer", transition: "all 0.2s", marginTop: 8 }}
            onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "#f8717130"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#444"; e.currentTarget.style.borderColor = "#1e1e1e"; }}
          >Logout</button>
        </div>

        {/* ── MEMBER BADGE ── */}
        <div style={{ opacity: vis ? 1 : 0, animation: vis ? "fadeUp 0.6s 0.12s ease both" : "none", marginBottom: 64 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 20px", background: "#4ade8010", border: "1px solid #4ade8025", borderRadius: "20px", boxShadow: "0 0 20px #4ade8010" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ade80", display: "inline-block", boxShadow: "0 0 10px #4ade80", animation: "pulse 2s ease-in-out infinite" }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: "#4ade80", letterSpacing: "0.16em", textTransform: "uppercase" }}>Approved Member</span>
          </div>
        </div>

        {/* ── LATEST CONTENT LABEL ── */}
        <div style={{ opacity: vis ? 1 : 0, animation: vis ? "fadeUp 0.6s 0.18s ease both" : "none", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.26em", textTransform: "uppercase", color: "#f5c518" }}>Latest Content</div>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,#f5c51830,transparent)", maxWidth: 300 }} />
          </div>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "#333", marginTop: 8, letterSpacing: "0.04em" }}>Most recent post from each section — click to read</p>
        </div>

        {/* ── MAIN CARDS GRID ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 20, marginBottom: 64 }} className="dash-grid">
          {sections.map((s, i) => {
            const post = posts[s.key];
            const postUrl = post ? `${s.basePath}/${post.slug}` : s.to;
            return (
              <div key={s.key} style={{ opacity: vis ? 1 : 0, animation: vis ? `fadeUp 0.6s ${0.2 + i * 0.1}s ease both` : "none" }}>
                <TiltCard>
                  <div style={{ background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: "14px", overflow: "hidden", transition: "border-color 0.3s, box-shadow 0.3s", position: "relative" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = s.color + "45"; e.currentTarget.style.boxShadow = `0 20px 60px #00000080, 0 0 40px ${s.color}12`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#1a1a1a"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div style={{ height: 2, background: `linear-gradient(90deg,${s.color},transparent)`, transformOrigin: "left", animation: `lineIn 0.8s ${i * 0.15}s both` }} />
                    <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: `radial-gradient(ellipse,${s.color}10 0%,transparent 70%)`, pointerEvents: "none" }} />

                    <div style={{ padding: "24px 24px 18px", borderBottom: "1px solid #141414", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: "10px", background: s.color + "12", border: `1px solid ${s.color}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", color: s.color, filter: `drop-shadow(0 0 10px ${s.color}80)`, animation: `floatY ${5 + i * 0.8}s ease-in-out infinite` }}>
                          {s.icon}
                        </div>
                        <div>
                          <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#e8e8e8" }}>{s.label}</div>
                          <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: "#333", letterSpacing: "0.1em", marginTop: 2 }}>{s.desc}</div>
                        </div>
                      </div>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: "8px", padding: "3px 10px", borderRadius: "20px", background: s.color + "10", border: `1px solid ${s.color}22`, color: s.color, letterSpacing: "0.12em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{s.tag}</span>
                    </div>

                    <div style={{ padding: "20px 24px 24px" }}>
                      {loading ? (
                        <div style={{ height: 60, background: "#111", borderRadius: "6px", animation: "shimmer 1.5s ease-in-out infinite" }} />
                      ) : post ? (
                        <>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                            <div style={{ width: 5, height: 5, borderRadius: "50%", background: s.color, boxShadow: `0 0 8px ${s.color}`, flexShrink: 0 }} />
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: "8px", color: "#333", letterSpacing: "0.12em", textTransform: "uppercase" }}>Latest</span>
                            {formatDate(post.createdAt) && (
                              <span style={{ fontFamily: "var(--font-mono)", fontSize: "8px", color: "#222", marginLeft: "auto" }}>{formatDate(post.createdAt)}</span>
                            )}
                          </div>
                          <Link to={postUrl} style={{ textDecoration: "none" }}>
                            <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "#d0d0d0", lineHeight: 1.35, marginBottom: 8, transition: "color 0.2s" }}
                              onMouseEnter={e => e.currentTarget.style.color = s.color}
                              onMouseLeave={e => e.currentTarget.style.color = "#d0d0d0"}
                            >{post.title}</div>
                          </Link>
                          {post.excerpt && (
                            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "#333", lineHeight: 1.7, marginBottom: 16, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{post.excerpt}</p>
                          )}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Link to={postUrl} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: s.color, textDecoration: "none", letterSpacing: "0.12em", textTransform: "uppercase", transition: "opacity 0.2s" }}
                              onMouseEnter={e => e.currentTarget.style.opacity = "0.7"}
                              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                            >Read Now →</Link>
                            <Link to={s.to} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: "#333", textDecoration: "none", letterSpacing: "0.1em", textTransform: "uppercase", transition: "color 0.2s" }}
                              onMouseEnter={e => e.currentTarget.style.color = "#555"}
                              onMouseLeave={e => e.currentTarget.style.color = "#333"}
                            >All Posts →</Link>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#1e1e1e", flexShrink: 0 }} />
                          <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "#222", letterSpacing: "0.08em" }}>No posts yet</span>
                          <Link to={s.to} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: s.color + "60", textDecoration: "none", marginLeft: "auto", letterSpacing: "0.1em", textTransform: "uppercase" }}>Browse →</Link>
                        </div>
                      )}
                    </div>
                  </div>
                </TiltCard>
              </div>
            );
          })}
        </div>

        {/* ── QUICK ACCESS STRIP ── */}
        <div style={{ opacity: vis ? 1 : 0, animation: vis ? "fadeUp 0.6s 0.55s ease both" : "none", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.26em", textTransform: "uppercase", color: "#818cf8" }}>Quick Access</div>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,#818cf830,transparent)", maxWidth: 300 }} />
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {sections.map((s, i) => (
              <Link key={s.to} to={s.to} style={{ textDecoration: "none", opacity: vis ? 1 : 0, animation: vis ? `fadeUp 0.5s ${0.6 + i * 0.07}s ease both` : "none" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", background: "#0a0a0a", border: `1px solid ${s.color}20`, borderRadius: "8px", transition: "all 0.22s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = s.color + "10"; e.currentTarget.style.borderColor = s.color + "45"; e.currentTarget.style.boxShadow = `0 0 20px ${s.color}12`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#0a0a0a"; e.currentTarget.style.borderColor = s.color + "20"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <span style={{ fontSize: "0.85rem", color: s.color, filter: `drop-shadow(0 0 6px ${s.color}80)` }}>{s.icon}</span>
                  <span style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#888" }}>{s.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* ── DISCLAIMER ── */}
        <div style={{ opacity: vis ? 1 : 0, animation: vis ? "fadeUp 0.6s 0.7s ease both" : "none", marginTop: 48 }}>
          <div style={{ padding: "20px 24px", background: "#0a0a0a", border: "1px solid #141414", borderLeft: "3px solid #f5c518", borderRadius: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#f5c518", display: "inline-block", boxShadow: "0 0 8px #f5c518" }} />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "8px", color: "#f5c518", letterSpacing: "0.18em", textTransform: "uppercase" }}>Disclaimer</span>
            </div>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "#2a2a2a", lineHeight: 1.8, margin: 0 }}>
              All content is for educational purposes only. Nothing here constitutes financial advice or trade recommendations. Always apply your own analysis and risk management.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
